<template>
  <div>
    <custom-header :status="failure" />
    <general-failure v-if="isGeneralFailure" :action-hide-message="hideMessageGeneralFailure" />
    <credit-card-failure v-if="isCreditCardFailure" :action-pay-with-bank-slip="goToBankSlipCheckout"
      :action-back-to-credit-card-payment="displayCreditCardPaymentCheckout"
      :credit-card-failure-message="creditCardFailureMessage"
      :credit-card-failure-acao-message="creditCardFailureAcaoMessage" />
    <div v-if="!failure" class="checkout page" :style="plansHeight">
      <black-friday-countdown class="checkout-page black-friday-countdown--desktop" />
      <form name="checkout-form" class="checkout-form" novalidate="true" @submit.prevent>
        <div class="checkout-form-body">
          <div id="planInfo" class="plan-info expanded">
            <div class="section-title">
              <p class="primary">Selecione o seu plano premium:</p>
              <div class="secondary" @click="togglePlans">
                <p v-if="userInfo" class="user-email" id="card-userEmail">
                  {{ userInfo.email }}
                </p>
              </div>
            </div>
            <div v-if="planOffer" class="plans-container open">
              <div class="availablePlans">Planos disponíveis</div>
              <div v-for="plan in availablePlans" :key="plan.id" class="plan" :class="{ discount: discountApplied }"
                @click.prevent="selectPlan(plan.id)">
                <input :id="plan.id" v-model="form.plan.value" type="radio" name="plan" :value="plan.id" />
                <label :for="plan.id" class="plan-card"
                  :class="[plan.class, { 'no-discount-tag': !planDiscountPercentage(plan) }]"
                  :data-discount-percentage="`-${planDiscountPercentage(plan)}%`">
                  <p class="plan-title">
                    <span class="period">{{ plan.title.period }}</span>
                    <span class="type">{{ plan.title.type }}</span>
                  </p>
                  <p class="price" :class="{ discount: discountApplied }">
                    <span class="full" v-if="plan.price.original !== plan.price.full">De R$ {{
                      numberToCurrency(plan.price.original) }}</span>
                    <span v-if="plan.price.original !== plan.price.full"> | </span>
                    <span>
                      <span class="withDiscount">Por R$ {{ numberToCurrency(discountApplied && plan.price.discount ?
                        plan.price.discount : plan.price.full) }}</span> <span v-if="plan.isSplittable"> | em até {{
                          form.installments.value }}x</span>
                    </span>
                  </p>
                </label>
              </div>
            </div>
            <div v-else class="plans-skeleton" aria-label="Carregando planos">
              <div class="plan-card-skeleton"></div>
              <div class="plan-card-skeleton"></div>
              <div class="plan-card-skeleton"></div>
            </div>
            <div class="about">
              <p v-html="aboutInfo.text" />
            </div>

            <div class="submit-form mobile">
              <custom-button :primary="true" :action="submitSubscription" :disabled="isSubmitButtonDisabled">
                Finalizar pagamento
              </custom-button>
            </div>
          </div>

          <div class="container">
            <div class="payment-info">
              <div class="info-container">
                <div class="address-block">
                  <div class="address-header">
                    <h3 class="address-title">Insira os dados de pagamento</h3>
                    <p class="address-description">
                      Para estar em conformidade com as regras de instituições financeiras vigentes,
                      e manter seus dados protegidos pela Lei Geral de Proteção de Dados Pessoais (LGPD),
                      precisamos pedir seu endereço atualizado.
                    </p>
                  </div>

                  <div class="address-fields">
                    <div class="address-field-cep" :class="{ 'address-field-cep--error': cepError }">
                      <form-input :name="'zipCode'" :value="form.zipCode.value" :disabled="form.zipCode.disabled"
                        @input="form.zipCode.value = $event; cepError = ''" @blur="fillAddress" />
                      <svg-image v-if="cepLoading" class="cep-spinner" file-name="icon-cep-spinner.svg"
                        alt="Carregando endereço" />
                      <span v-if="cepError" class="cep-error">{{ cepError }}</span>
                    </div>

                    <div class="address-row address-row--state-city">
                      <div class="form-input address-select">
                        <select id="state" v-model="form.state.value" name="state" :disabled="form.state.disabled"
                          :class="{ 'has-value': form.state.value }">
                          <option value="" disabled hidden>Estado</option>
                          <option v-for="uf in statesList" :key="uf.sigla" :value="uf.sigla">
                            {{ uf.sigla === form.state.value ? uf.sigla : uf.nome }}
                          </option>
                        </select>
                        <label for="state">Estado</label>
                      </div>
                      <form-input :name="'city'" :value="form.city.value" :disabled="form.city.disabled"
                        @input="form.city.value = $event" />
                    </div>

                    <form-input :name="'neighborhood'" :value="form.neighborhood.value"
                      :disabled="form.neighborhood.disabled" @input="form.neighborhood.value = $event" />

                    <form-input :name="'street'" :value="form.street.value" :disabled="form.street.disabled"
                      @input="form.street.value = $event" />

                    <div class="address-row">
                      <div class="address-number-group">
                        <form-input :name="'number'" :value="form.number.value" :disabled="form.number.disabled"
                          @input="form.number.value = $event" />
                        <label class="address-checkbox">
                          <input type="checkbox" v-model="form.noNumber" @change="onNoNumberChange" />
                          <span class="address-checkbox__box"></span>
                          <span class="address-checkbox__label">Sem número</span>
                        </label>
                      </div>
                      <div class="address-complement-group">
                        <form-input :name="'complement'" :value="form.complement.value"
                          :disabled="form.complement.disabled" @input="form.complement.value = $event" />
                        <span class="address-complement-helper">Opcional</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="payment-brands">
                  <div class="payment-brands__gateway">
                    <span>Pagamentos por</span>
                    <svg-image class="payment-brands__iugu" file-name="icon-iugu.svg"
                      alt="Ícone do meio de pagamento IUGU" />
                  </div>
                  <div class="payment-brands__flags">
                    <svg-image class="payment-brands__flag" file-name="icon-master-card.svg"
                      alt="Ícone do cartão MasterCard" />
                    <svg-image class="payment-brands__flag" file-name="icon-dinners-club.svg"
                      alt="Ícone do cartão DinnersClub" />
                    <svg-image class="payment-brands__flag" file-name="icon-american-express.svg"
                      alt="Ícone do cartão AmericanExpress" />
                    <svg-image class="payment-brands__flag" file-name="icon-visa.svg" alt="Ícone do cartão Visa" />
                    <svg-image class="payment-brands__flag" file-name="icon-elo.svg" alt="Ícone do cartão Elo" />
                  </div>
                </div>

                <div class="card-fields">
                  <form-input :name="'cardNumber'" :value="form.cardNumber.value"
                    @input="form.cardNumber.value = $event" @blur="
                      trackSnowplow('sendSnowplowSubscribeEvent', {
                        form_element: form.cardNumber,
                        step_name: 'user_card_number',
                        step_num: 1,
                      })
                      " />
                  <div class="row">
                    <form-input :name="'cardExpirationDate'" :value="form.cardExpirationDate.value"
                      @input="form.cardExpirationDate.value = $event" @blur="
                        trackSnowplow('sendSnowplowSubscribeEvent', {
                          form_element: form.cardExpirationDate,
                          step_name: 'user_card_expiration',
                          step_num: 2,
                        })
                        " />
                    <form-input :name="'cardVerificationValue'" :value="form.cardVerificationValue.value"
                      @input="form.cardVerificationValue.value = $event" @blur="
                        trackSnowplow('sendSnowplowSubscribeEvent', {
                          form_element: form.cardVerificationValue,
                          step_name: 'user_card_cvv',
                          step_num: 3,
                        })
                        " />
                  </div>
                  <form-input :name="'cardName'" :value="form.cardName.value" @input="form.cardName.value = $event"
                    @blur="trackerSnowplowAndBrazeByUserCardName(form)" />
                  <form-input :name="'cpf'" :value="form.cpf.value" :disabled="form.cpf.disabled"
                    @input="form.cpf.value = $event" @blur="
                      trackSnowplow('sendSnowplowSubscribeEvent', {
                        form_element: form.cpf,
                        step_name: 'user_cpf',
                        step_num: 5,
                      })
                      " />
                </div>
                <div v-if="planOffer && acceptCoupon" class="discount-coupon">
                  <form-input v-if="planOffer && acceptCoupon" :name="'discountCoupon'"
                    :value="form.discountCoupon.value" :disabled="form.discountCoupon.disabled"
                    :readonly="form.discountCoupon.readonly" :change="handleCouponValue"
                    @input="form.discountCoupon.value = $event" />
                  <button v-if="!discountApplied && !form.discountCoupon.readonly" type="button"
                    @click="applyDiscountCoupon(form.discountCoupon.value, planOffer)">
                    Aplicar
                  </button>
                  <button v-if="discountApplied && !form.discountCoupon.readonly" type="button"
                    @click="removeDiscountApplied(form.discountCoupon)">
                    X
                  </button>
                </div>
                <div v-if="planOffer && acceptCoupon" class="discount-coupon-info">
                  Desconto não acumulativo. Ao aplicar valerá como o principal.
                </div>
                <div v-if="splittingEnabled" class="form-input installments">
                  <select id="installments" v-model="form.installments.value" name="installments" @blur="
                    trackSnowplow('sendSnowplowSubscribeEvent', {
                      form_element: form.installments,
                      step_name: 'user_installments',
                      step_num: 7,
                      step_value: `${form.installments.value} installments`,
                    })
                    " @change="changeInstallments">
                    <option v-for="item in form.installments.options" :key="item.value" :value="item.value">
                      {{ item.value }}x de R$ {{ numberToCurrency(item.price) }} sem juros
                    </option>
                  </select>
                  <label for="installments">Número de parcelas</label>
                </div>
              </div>
            </div>

            <div class="purchase-summary">
              <div class="summary">
                <div>
                  <p>Resumo da compra</p>
                </div>
                <div :key="summaryPlan.id" v-for="summaryPlan in applySummary()">
                  <p class="summary-price">
                    <span class="full">R${{ numberToCurrency(summaryPlan.price.original) }}</span> |

                    <span v-if="discountApplied">R${{ numberToCurrency(summaryPlan.price.discount) }}</span>
                    <span v-else>R${{ numberToCurrency(summaryPlan.price.full) }}</span>
                  </p>
                  <p v-if="summaryPlan.isSplittable && splittingEnabled" class="summary-price-description"></p>
                </div>
              </div>

              <div class="checkout-form-button">
                <custom-button :primary="true" :action="submitSubscription" :disabled="isSubmitButtonDisabled"
                  id="checkout-card-submitButton">
                  Finalizar pagamento
                </custom-button>
                <span class="recaptcha-disclaimer-desktop">
                  This site is protected by reCAPTCHA and the Google
                  <a href="https://policies.google.com/privacy">Privacy Policy</a> and
                  <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                </span>
              </div>
            </div>

            <div class="checkout-form-button-desktop">
              <custom-button :primary="true" :action="submitSubscription" :disabled="isSubmitButtonDisabled"
                id="checkout-card-submitButton">
                Finalizar pagamento
              </custom-button>
              <span class="recaptcha-disclaimer-desktop">
                This site is protected by reCAPTCHA and the Google
                <a href="https://policies.google.com/privacy">Privacy Policy</a> and
                <a href="https://policies.google.com/terms">Terms of Service</a> apply.
              </span>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import SVGImage from '@/components/SVGImage';
import CustomButton from '@/components/CustomButton';
import FormInput from '@/components/FormInput';
import IuguService from '@/services/IuguService';
import SubscriptionService from '@/services/SubscriptionService';
import OfferService from '@/services/OfferService';
import Utils from '@/assets/js/Utils';
import CreditCardFailure from '@/views/credit-card-failure';
import GeneralFailure from '@/views/general-failure';
import CustomHeader from '@/components/CustomHeader';
import BlackFridayCountdown from '@/components/BlackFridayCountdown';
import UserSessionService from '@/services/user-session';
import UserService from '@/services/UserService';
import ZipCodeService from '@/services/zip-code';
import MonthlyExclusiveCoupon from '@/services/monthly-exclusive-coupon';

import LanguageString from '@/config/language/pt-BR';
import constants from '@/constants';

import * as Sentry from '@sentry/browser';

export default {
  name: 'Checkout',
  components: {
    'svg-image': SVGImage,
    'custom-button': CustomButton,
    'form-input': FormInput,
    'credit-card-failure': CreditCardFailure,
    'custom-header': CustomHeader,
    'general-failure': GeneralFailure,
    'black-friday-countdown': BlackFridayCountdown,
  },
  data() {
    return {
      isSubmitButtonDisabled: false,
      splittingEnabled: false,
      acceptCoupon: false,
      isMonthlyCouponCampaign: false,
      windowWidth: 0,
      languageString: LanguageString,
      isCreditCard: true,
      isCreditCardFailure: false,
      isCardSubscriptionError: false,
      isGeneralFailure: false,
      creditCardFailureMessage: null,
      creditCardFailureAcaoMessage: null,
      plansHeight: { '--plans-height': '0px' },
      statesList: [],
      cepLoading: false,
      cepError: '',
      aboutInfo: {
        show: false,
        text:
          'Será feita uma pré-autorização no valor total para validar o limite do cartão. Ela é retirada da fatura após a cobrança da assinatura. </br></br>Os planos possuem renovação automática, cancele quando quiser. Descontos obtidos na contratação não são recorrentes.',
      },
      couponDiscountApplied: false,
      discountApplied: false,
      discountPercentage: 0,
      discountCouponValue: null,
      basePrice: process.env.VUE_APP_BASE_PRICE,
      planOffer: null,
      planOffers: {
        show: false,
      },
      summaryPlanId: null,
      userSession: null,
      failure: false,
      form: {
        cardNumber: {
          value: '',
          disabled: false,
        },
        cardExpirationDate: {
          value: '',
          disabled: false,
        },
        cardVerificationValue: {
          value: '',
          disabled: false,
        },
        cardName: {
          value: '',
          disabled: false,
        },
        cpf: {
          value: '',
          disabled: false,
        },
        discountCoupon: {
          value: '',
          disabled: false,
        },
        installments: {
          value: 12,
          options: [],
        },
        plan: {
          value: process.env.VUE_APP_DEFAULT_PLAN_ID,
        },
        zipCode: {
          value: '',
          disabled: false,
        },
        state: {
          value: '',
          disabled: false,
        },
        city: {
          value: '',
          disabled: true,
        },
        neighborhood: {
          value: '',
          disabled: true,
        },
        street: {
          value: '',
          disabled: true,
        },
        number: {
          value: '',
          disabled: true,
        },
        complement: {
          value: '',
          disabled: true,
        },
        noNumber: false,
      },
    };
  },
  mounted() {
    this.clarity();
  },
  computed: {
    partPlan() {
      return this.planOffer.options.find(item => item.isSplittable);
    },
    userInfo() {
      return this.userSession ? this.userSession.currentUser : null;
    },
    availablePlans() {
      return this.planOffer.options.filter(plan => this.isCreditCard || plan.id !== this.partPlan.id);
    },
    handleCouponValue() {
      const hasError = document.querySelector('input[name=discount-coupon]');

      if (
        (this.form.discountCoupon.value === '' || this.form.discountCoupon.value.length < 10) &&
        hasError !== null
      ) {
        const info = document.querySelector('.discount-coupon-info');
        document.querySelector('.discount-coupon')?.classList.remove('form-input-error');
        info?.classList.remove('discount-coupon-invalid');
        if (info) info.textContent = `Desconto não acumulativo. Ao aplicar valerá como o principal.`;
      }
    },
  },
  async created() {
    if (UserSessionService.isSubscriber()) {
      this.$router.push({
        name: 'subscription-info',
        query: this.$route.query,
      });
      return;
    }

    this.loadStates();

    try {
      this.userSession = UserSessionService.getData() || UserSessionService.getAuthenticationCookieData();
      this.isMonthlyCouponCampaign = MonthlyExclusiveCoupon.isCampaignQuery(this.$route.query);

      const offersData = (await SubscriptionService.getOffersByGateway()).data;

      this.planOffers = {
        show: false,
        offers: {
          default: {
            isBlackFriday: offersData.backgroundImage === 'black_friday',
            discountCouponCode: offersData.plans.list[0].discountCouponCode,
            options: [],
          },
        },
      };

      const planClasses = ['first-card', 'second-card', 'third-card'];

      for (let key in offersData.plans.list) {
        const currentItem = offersData.plans.list[key];
        const period = currentItem.period;
        const fullPrice = this.currencyToNumber(offersData.plans.basePrice[period]);
        const priceWithBaseDiscount = fullPrice - (fullPrice / 100) * currentItem.baseDiscountPercentage;
        let name = 'Mensal';
        let type = 'À vista';
        if (period === 'annually') {
          name = `Anual ${currentItem.subtitle.text1.toLowerCase()}`;
          type = currentItem.subtitle.text1;
        }
        const item = {
          id: currentItem.storeId,
          name,
          class: planClasses[key],
          title: {
            period: currentItem.title,
            type,
          },
          isSplittable: currentItem.splittable,
          price: {
            original: this.currencyToNumber(offersData.plans.basePrice[period]),
            full: priceWithBaseDiscount,
            discount: null,
            discountPercentage: currentItem.discountPercentage,
            originalDiscountPercentage: Math.round(currentItem.baseDiscountPercentage),
          },
          periodicity: period,
        };

        this.planOffers.offers.default.options.push(item);
      }

      this.form.plan.value = this.$route.query.planid || offersData.plans.list[1].storeId;
      this.planOffer = this.getPlanOfferByPlan(this.form.plan.value);

      this.setCPF();

      await this.loadUserAddress(this.userInfo.id);

      this.acceptCoupon = this.isCouponAllowedForPlan(this.form.plan.value);

      const queryCoupon = this.$route.query.couponCode;
      const initialCoupon =
        queryCoupon && this.canApplyCouponToPlan(queryCoupon, this.form.plan.value)
          ? queryCoupon
          : this.planOffers.offers.default.discountCouponCode;

      if (initialCoupon) {
        this.applyDiscountCoupon(initialCoupon, this.planOffers);
      }

      await this.generateInstallmentOptions(this.partPlan.price.full);

      this.$nextTick(() => {
        if (window && window.hasOwnProperty('addEventListener')) {
          window.addEventListener('resize', this.getWindowWidth);
        }

        this.getWindowWidth();
      });

      this.summaryPlanId = this.form.plan.value;
      this.setSplittingEnabled(this.form.installments);
      this.trackBrazeWhenLoadPage();
    } catch (error) {
      if (error.response) {
        error = error.response.data;
      }

      let errorMessage = error.message || error.mensagem;
      Sentry.captureException(error);

      if (errorMessage) {
        if (
          error.code === 'AUTH_CODE_EXPIRED' ||
          error.code === 'AUTH_INVALID_AUTH_CODE' ||
          error.code === 'AUTH_WRONG_TOKEN_TYPE'
        ) {
          UserSessionService.removeData();
          this.createToast(errorMessage, {
            redirectTo: 'login',
          });
        } else if (errorMessage.includes('getAuthenticationCookieData')) {
          UserSessionService.removeData();
          errorMessage = 'Desculpe, não encontramos a sua sessão. Efetue o login novamente.';
          this.createToast(errorMessage, {
            redirectTo: 'login',
          });
        } else {
          this.createToast(errorMessage);
        }
      } else {
        errorMessage =
          'Ocorreu um erro ao buscar as ofertas. Tente novamente mais tarde ou entre em contato pelo número +55(21) 97376-4379';
        this.createToast(errorMessage);
      }
    } finally {
      this.finishLoading();
    }
  },
  updated() {
    this.changeInstallments();
    this.applySummary();
  },

  methods: {
    numberToCurrency: Utils.numberToCurrency,
    currencyToNumber: Utils.currencyToNumber,

    isCampaignMonthlyPlan(planId) {
      return this.isMonthlyCouponCampaign && MonthlyExclusiveCoupon.isCampaignPlan(planId);
    },

    isCouponAllowedForPlan(planId) {
      if (this.isCampaignMonthlyPlan(planId)) return true;
      if (constants.HIDE_COUPON_PLAN_IDS.includes(planId)) return false;

      const plan = this.planOffers?.offers?.default?.options.find(option => option.id === planId);

      return plan ? plan.periodicity === 'annually' : false;
    },

    // O cupom exclusivo só vale no mensal da campanha, e o mensal da campanha só aceita esse cupom.
    canApplyCouponToPlan(coupon, planId) {
      return MonthlyExclusiveCoupon.isExclusiveCoupon(coupon) === this.isCampaignMonthlyPlan(planId);
    },

    rejectCouponForPlan(coupon) {
      const couponInput = document.querySelector('#discount-coupon');
      if (couponInput) couponInput.value = '';
      this.form.discountCoupon.value = '';

      this.createToast(
        MonthlyExclusiveCoupon.isExclusiveCoupon(coupon)
          ? MonthlyExclusiveCoupon.INVALID_PLAN_MESSAGE
          : 'Cupom inválido para o plano selecionado.',
      );
    },

    planDiscountPercentage(plan) {
      const percentage = this.discountApplied ? plan.price.discountPercentage : plan.price.originalDiscountPercentage;

      return Math.round(percentage) || 0;
    },

    applySummary() {
      if (this.planOffer) {
        return this.planOffer.options.filter(plan => plan.id === this.summaryPlanId);
      }
    },

    validateCreditCardForm() {
      const { cardNumber, cardExpirationDate, cardVerificationValue, cardName, cpf, discountCoupon } = this.form;
      const inputRadioPlans = document.getElementsByName('plan');
      const inputRadioPlansSelected = Array.from(inputRadioPlans).filter(i => i.checked && i);
      const result = {
        isValid: false,
      };

      if (discountCoupon.value !== '' && !this.discountApplied && this.isCouponAllowedForPlan(this.form.plan.value)) {
        result.error = {
          field: 'discountCoupon',
          message: 'Cupom não aplicado',
        };

        return result;
      }

      if (!cardNumber.value || !cardNumber.isValid || !IuguService.validateCardNumber(cardNumber.value)) {
        result.error = {
          field: 'cardNumber',
          message: 'Número de cartão inválido',
        };

        return result;
      }

      if (
        !cardExpirationDate.value ||
        !cardExpirationDate.isValid ||
        !IuguService.validateCardExpirationDate(cardExpirationDate.value)
      ) {
        result.error = {
          field: 'cardExpirationDate',
          message: 'Data de expiração do cartão inválida',
        };

        return result;
      }

      if (
        !cardVerificationValue.value ||
        !cardVerificationValue.isValid ||
        !IuguService.validateCardVerificationValue(cardNumber.value, cardVerificationValue.value)
      ) {
        result.error = {
          field: 'cardVerificationValue',
          message: 'Código de verificação do cartão inválido',
        };

        return result;
      }

      if (!cardName.value || !cardName.isValid) {
        result.error = {
          field: 'cardName',
          message: 'Nome do titular inválido',
        };

        return result;
      }

      if (!cpf.value || !cpf.isValid || !Utils.validateCPF(cpf.value)) {
        result.error = {
          field: 'cpf',
          message: 'CPF inválido',
        };

        return result;
      }

      if (!this.form.zipCode.value) {
        result.error = {
          field: 'zipCode',
          message: 'CEP obrigatório',
        };
        return result;
      }

      if (!this.form.state.value) {
        result.error = {
          field: 'state',
          message: 'Estado obrigatório',
        };
        return result;
      }

      if (!this.form.city.value) {
        result.error = {
          field: 'city',
          message: 'Município obrigatório',
        };
        return result;
      }

      if (!this.form.neighborhood.value) {
        result.error = {
          field: 'neighborhood',
          message: 'Bairro obrigatório',
        };
        return result;
      }

      if (!this.form.street.value) {
        result.error = {
          field: 'street',
          message: 'Endereço obrigatório',
        };
        return result;
      }

      if (!this.form.noNumber && !this.form.number.value) {
        result.error = {
          field: 'number',
          message: 'Número obrigatório',
        };
        return result;
      }

      if (inputRadioPlansSelected.length === 0) {
        result.error = {
          field: 'Planos',
          message: 'Plano não selecionado',
        };

        return result;
      }

      result.isValid = true;

      return result;
    },
    getPlanOfferByPlan(planId) {
      let planOffer = null;

      for (let key in this.planOffers.offers) {
        let offer = this.planOffers.offers[key];

        if (offer.options.find(planOption => planOption.id === planId)) planOffer = offer;
      }

      return planOffer || this.planOffers.offers['default'];
    },
    getWindowWidth() {
      this.windowWidth = document.documentElement.clientWidth;

      if (this.windowWidth < 600) this.scrollToPlan('auto');
    },
    scrollToPlan(behavior) {
      const plansElement = document.querySelector(`#${this.form.plan.value}`);

      if (plansElement) {
        const planCardClasses = plansElement?.labels[0]?.classList?.value;
        let amount = this.windowWidth;

        if (/first/.test(planCardClasses)) amount *= -1;

        document.querySelector('.plans-container')?.scrollTo({ left: amount, behavior });
      }
    },
    selectPlan(planId) {
      if (planId !== this.form.plan.value) {
        this.chooseSubscriptionPlan(planId, this.partPlan.id === planId ? 'annually_splittable' : 'annually_onetime');
      }

      this.form.plan.value = planId;
      this.summaryPlanId = planId;
      this.setSplittingEnabled(this.form.installments);
      this.acceptCoupon = this.isCouponAllowedForPlan(planId);

      this.resetDiscountCoupon();

      if (this.isCampaignMonthlyPlan(planId)) {
        this.applyDiscountCoupon(MonthlyExclusiveCoupon.COUPON_CODE, this.planOffer);
      }

      if (this.windowWidth < 600) {
        this.scrollToPlan('smooth');
      }
    },
    togglePlans() {
      return (this.planOffers.show = !this.planOffers.show);
    },
    applyDiscountCoupon(discountCoupon, planOffer) {
      if (!discountCoupon) return;
      if (!this.isCouponAllowedForPlan(this.form.plan.value)) return;

      if (!this.canApplyCouponToPlan(discountCoupon, this.form.plan.value)) {
        this.rejectCouponForPlan(discountCoupon);
        return;
      }

      this.form.discountCoupon.value = discountCoupon;

      this.initLoading();

      OfferService.validateCoupon(
        `Bearer ${UserSessionService.getAuthenticationToken()}`,
        discountCoupon,
        this.userInfo.id,
      )
        .then(response => {
          if (response.data.discountPercentage === 0 || !response.data.discountPercentage) {
            this.form.discountCoupon.isValid = false;
            this.discountApplied = false;

            const info = document.querySelector('.discount-coupon-info');
            document.querySelector('.discount-coupon')?.classList.add('form-input-error');
            info?.classList.add('discount-coupon-invalid');
            if (info) info.textContent = `Esse não é um cupom de desconto`;
          } else {
            const isExclusiveCoupon = MonthlyExclusiveCoupon.isExclusiveCoupon(discountCoupon);

            this.planOffer.options.forEach(option => {
              const receivesDiscount = isExclusiveCoupon
                ? this.isCampaignMonthlyPlan(option.id)
                : option.periodicity === 'annually';

              if (receivesDiscount) {
                option.price.discountPercentage = response.data.discountPercentage;
                const discountedPlan = option.price.original * ((100 - response.data.discountPercentage) / 100);
                option.price.discount = +discountedPlan.toFixed(2);
              } else {
                option.price.discount = null;
                option.price.discountPercentage = option.price.originalDiscountPercentage;
              }
            });

            this.form.discountCoupon.isValid = true;
            this.form.discountCoupon.disabled = true;
            this.discountPercentage = `-${response.data.discountPercentage}%`;
            this.discountApplied = true;
            this.discountCouponValue = discountCoupon;
            this.couponDiscountApplied = true;

            this.generateInstallmentOptions(this.partPlan.price.discount || this.partPlan.price.full);

            const info = document.querySelector('.discount-coupon-info');
            document.querySelector('input[name=discount-coupon]')?.classList.add('formDiscountApplied');
            info?.classList.add('discount-coupon-info-applied');
            if (info) info.textContent = `Cupom aplicado - ${response.data.discountPercentage}% de desconto`;

            info?.classList.remove('discount-coupon-invalid');
            document.querySelector('.discount-coupon')?.classList.remove('form-input-error');

            this.trackSnowplow('sendSnowplowSubscribeEvent', {
              form_element: this.form.discountCoupon,
              step_name: 'user_coupon',
              step_num: 6,
              step_value: discountCoupon,
            });
          }
        })
        .catch(error => {
          const statusCode = error.response?.status;

          if (error.response) {
            error = error.response.data;
          }
          const errorMessage =
            error.mensagem ||
            error.message ||
            'Ocorreu um erro ao validar o cupom. Tente novamente mais tarde ou entre em contato pelo número +55(21) 97376-4379';

          Sentry.captureException(error);

          if (
            statusCode === 401 ||
            statusCode === 403 ||
            error.code === 'AUTH_CODE_EXPIRED' ||
            error.code === 'AUTH_INVALID_AUTH_CODE' ||
            error.code === 'AUTH_WRONG_TOKEN_TYPE'
          ) {
            UserSessionService.removeData();
            this.finishLoading();
            this.createToast(errorMessage, {
              redirectTo: 'login',
            });
          } else if (statusCode === 422 && error.code && error.code.includes('COUPON_')) {
            const couponInput = document.querySelector('#discount-coupon');
            if (couponInput) couponInput.value = '';
            this.form.discountCoupon.value = '';
            this.createToast(errorMessage);
          } else {
            this.createToast(errorMessage);
          }
        })
        .finally(() => {
          this.changeInstallments();
          this.finishLoading();
        });
    },
    resetDiscountCoupon() {
      this.form.discountCoupon.value = '';
      this.form.discountCoupon.disabled = false;
      this.discountApplied = false;
      this.discountPercentage = false;
      this.couponDiscountApplied = false;

      this.planOffer.options.forEach(option => {
        option.price.discount = null;
        option.price.discountPercentage = 0;
      });

      this.generateInstallmentOptions(this.partPlan.price.full);
      this.changeInstallments();

      const info = document.querySelector('.discount-coupon-info');
      document.querySelector('input[name=discount-coupon]')?.classList.remove('formDiscountApplied');
      document.querySelector('.discount-coupon')?.classList.remove('form-input-error');
      info?.classList.remove('discount-coupon-info-applied');
      info?.classList.remove('discount-coupon-invalid');
      if (info) info.textContent = 'Desconto não acumulativo. Ao aplicar valerá como o principal.';
    },
    removeDiscountApplied(formElement) {
      this.resetDiscountCoupon();

      this.trackSnowplow('sendSnowplowSubscribeEvent', {
        form_element: formElement,
        step_name: 'user_coupon',
        step_num: 6,
      });
    },
    generateInstallmentOptions(price) {
      if (this.form.installments) {
        this.form.installments.options = [];

        for (let i = 2; i <= 12; i++) {
          this.form.installments.options.push({
            value: i,
            price: (price / i).toFixed(2),
          });
        }
      }
    },

    async getRecaptchaEnterpriseToken() {
      return new Promise((resolve, reject) => {
        grecaptcha.enterprise.ready(() => {
          grecaptcha.enterprise
            .execute('6Lf2PlEnAAAAAPupTQNC4fAXZDCbGk7gm9Wu4_Nn', { action: 'CHECKOUT' })
            .then(token => {
              if (token) {
                resolve(token);
              } else {
                reject('Falha na geração do token reCAPTCHA');
              }
            });
        });
      });
    },

    async submitSubscription(event) {
      event.preventDefault();
      this.isSubmitButtonDisabled = true;
      this.initLoading();

      try {
        const formValidation = this.validateCreditCardForm();
        if (!formValidation.isValid) {
          this.finishLoading();
          this.createToast(formValidation.error.message);
          return;
        }

        const recaptchaToken = await this.getRecaptchaEnterpriseToken();

        const preAuthorizationInfo = await this.getPreAuthorizationSubscriptionBodyRequest();
        const preAuthorizationResult = await SubscriptionService.submitPreAuthorizationSubscription(
          preAuthorizationInfo,
          recaptchaToken,
        );

        const activateSubscriptionInfo = this.getActivateSubscriptionBodyRequest(
          preAuthorizationInfo,
          preAuthorizationResult,
        );

        await SubscriptionService.submitActivateSubscription(activateSubscriptionInfo);

        this.storeSubscriptionData();

        this.sendSuccessfulSubscriptionEvent();

        await SubscriptionService.changeUserStateToSubscriber();

        this.trackBrazeWhenSuccessPay();

        this.goToCreditCardCongratulationsScreen();
      } catch (error) {
        this.sendSubscriptionFailureEvent();

        if (error.bug_iugu_criacao_token) {
          this.recaptchaValidation = false;
          this.isCreditCardFailure = true;
          this.isGeneralFailure = true;
          this.failure = true;
        }

        if (error.payment_token_creation) {
          this.createToast('Erro no cadastramento do cartão.');
          return;
        }

        if (error.response) {
          error = error.response.data;
        }

        if (error.code === 'RECAPTCHA_ERROR') {
          this.createToast(error.message);
          return;
        }

        if (error.code === 'SUBSCRIPTION_ERRO_CUPOM_UTILIZADO') {
          this.createToast('Este cupom de desconto já foi ativado :(');
          return;
        }

        if (error.code === 'INFO_SUBSCRIPTION_IN_BILLING_RETRY') {
          this.createToast(this.languageString.GENERAL_FAILURE_billingRetry);
          Sentry.captureException(error);
          return;
        }

        let errorMessage;
        if (error) {
          errorMessage = error.mensagem || error.message;
        }

        if (errorMessage) {
          if (
            error.code === 'AUTH_CODE_EXPIRED' ||
            error.code === 'AUTH_INVALID_AUTH_CODE' ||
            error.code === 'AUTH_WRONG_TOKEN_TYPE'
          ) {
            UserSessionService.removeData();
            this.createToast(errorMessage, {
              redirectTo: 'login',
            });
          } else if (
            error.error &&
            error.error.code &&
            error.error.code === 'SUBSCRIPTION_CREDIT_CARD_TRANSACTION_UNAUTHORIZED'
          ) {
            this.trackBrazeWhenError();
            this.isCardSubscriptionError = true;
            this.isCreditCardFailure = true;
            this.failure = true;
            this.creditCardFailureMessage = errorMessage;
          } else if (error.code && error.details) {
            this.trackBrazeWhenError();
            this.isCreditCardFailure = true;
            this.failure = true;
            let failureMessage = errorMessage;
            if (error.details && error.details.acao) {
              failureMessage = `${failureMessage}`;
              this.creditCardFailureAcaoMessage = error.details.acao;
            }
            this.creditCardFailureMessage = failureMessage;
          } else if (error.message) {
            this.createToast(error.message);
          } else if (errorMessage === 'Network Error') {
            this.createToast(this.languageString.GENERAL_FAILURE_errorTitle);
          } else {
            this.trackBrazeWhenError();
            this.createToast(this.languageString.GENERAL_FAILURE_subTitle);
          }
        } else {
          this.isGeneralFailure = true;
          this.failure = true;
        }
      } finally {
        this.isSubmitButtonDisabled = false;
        this.finishLoading();
      }
    },
    storeSubscriptionData() {
      const currentPlan = this.planOffer.options.find(planOffer => planOffer.id === this.form.plan.value);
      let price;
      if (this.discountApplied && currentPlan.price.discount) {
        price = currentPlan.price.discount;
      } else {
        price = currentPlan.price.full;
      }

      window.sessionStorage.setItem('planName', currentPlan.name);
      window.sessionStorage.setItem('totalValue', this.numberToCurrency(price));

      if (this.form.installments) {
        const installmentValue =
          this.form.installments.value > 1
            ? this.numberToCurrency((price / this.form.installments.value).toFixed(2))
            : null;
        window.sessionStorage.setItem('installmentValue', installmentValue);

        window.sessionStorage.setItem('installmentQuantity', this.form.installments.value);
      }
      window.sessionStorage.setItem('userCpf', this.form.cpf.value);
      window.sessionStorage.setItem('userEmail', this.userInfo.email);
    },
    goToBankSlipCheckout() {
      let routerBankSlip = { name: 'PAYMENT_TYPE_BANK_SLIP' };
      this.isCreditCardFailure = !this.isCreditCardFailure;
      this.failure = false;
      this.setCPF();

      if (this.discountCouponValue && this.discountApplied) {
        this.form.discountCoupon.value = this.discountCouponValue;
        this.applyDiscountCoupon(this.discountCouponValue, this.planOffer.options);
        routerBankSlip = {
          ...routerBankSlip,
          query: { couponCode: this.discountCouponValue },
        };
      }

      this.trackSnowplow('trackButton', {
        button_name: 'payment_method',
        additional_properties: 'bank_slip',
      });

      this.$router.push(routerBankSlip);
    },
    displayCreditCardPaymentCheckout() {
      const clearCreditCardData = () => {
        const { cardNumber, cardExpirationDate, cardVerificationValue, cardName } = this.form;

        cardNumber.value = '';
        cardExpirationDate.value = '';
        cardVerificationValue.value = '';
        cardName.value = '';
      };

      clearCreditCardData();
      this.isCreditCardFailure = !this.isCreditCardFailure;
      this.failure = false;
      this.isCreditCard = true;

      this.trackSnowplow('trackButton', {
        button_name: 'payment_method',
        additional_properties: 'credit_card',
      });
    },
    setSplittingEnabled(formInstallments) {
      this.splittingEnabled = this.form.plan.value === this.partPlan.id;

      if (this.splittingEnabled) {
        this.trackSnowplow('sendSnowplowSubscribeEvent', {
          form_element: formInstallments,
          step_name: 'user_installments',
          step_num: 7,
          step_value: `${formInstallments.value} installments`,
        });
      }
    },
    setCPF() {
      if (this.userInfo && this.userInfo.cpf) {
        this.form.cpf.value = Utils.maskCPF(this.userInfo.cpf);
        this.form.cpf.isValid = true;
      }
    },

    async loadStates() {
      try {
        const states = await UserService.getStates();
        this.statesList = Array.isArray(states) ? states : [];
      } catch (_err) { }
    },

    async loadUserAddress(userId) {
      try {
        const userData = await UserService.getUserData(userId);
        const endereco = userData && userData.endereco;
        if (endereco) {
          this.form.street.value = endereco.logradouro || '';
          this.form.complement.value = endereco.comp || '';
          this.form.neighborhood.value = endereco.bairro || '';
          this.form.city.value = endereco.cidade || '';
          this.form.state.value = endereco.uf || '';
          this.form.zipCode.value = endereco.cep || '';

          const numero = (endereco.numero || '').toString().trim();
          const semNumero = numero === '' || numero.toUpperCase() === 'S/N';
          this.form.noNumber = semNumero;
          this.form.number.value = semNumero ? '' : numero;

          this.updateAddressFieldsAccess();
        }
      } catch (_err) { }
    },

    updateAddressFieldsAccess() {
      const unlocked = !!(this.form.zipCode.value && this.form.state.value);
      this.form.city.disabled = !unlocked;
      this.form.neighborhood.disabled = !unlocked;
      this.form.street.disabled = !unlocked;
      this.form.complement.disabled = !unlocked;
      this.form.number.disabled = !unlocked || this.form.noNumber;
    },

    async fillAddress() {
      this.cepError = '';
      if (!this.form.zipCode.value) {
        return;
      }
      this.cepLoading = true;
      try {
        const { data: zipCodeData } = await ZipCodeService.getZipCodeInfo(
          Utils.sanitizeDigitsString(this.form.zipCode.value),
        );
        if (!zipCodeData || zipCodeData.error || !zipCodeData.city) {
          this.setCepInvalid();
          return;
        }
        this.form.zipCode.isValid = true;
        if (zipCodeData.address) {
          this.form.street.value = zipCodeData.address;
          this.form.street.isValid = true;
          const streetEl = document.getElementsByName('street')[0];
          if (streetEl) streetEl.value = zipCodeData.address;
        }
        if (zipCodeData.district) {
          this.form.neighborhood.value = zipCodeData.district;
          this.form.neighborhood.isValid = true;
          const neighborhoodEl = document.getElementsByName('neighborhood')[0];
          if (neighborhoodEl) neighborhoodEl.value = zipCodeData.district;
        }
        if (zipCodeData.city) {
          this.form.city.value = zipCodeData.city;
          this.form.city.isValid = true;
          const cityEl = document.getElementsByName('city')[0];
          if (cityEl) cityEl.value = zipCodeData.city;
        }
        if (zipCodeData.uf) {
          this.form.state.value = zipCodeData.uf;
        }
        this.updateAddressFieldsAccess();
      } catch (error) {
        this.setCepInvalid();
      } finally {
        this.cepLoading = false;
      }
    },
    setCepInvalid() {
      this.form.zipCode.isValid = false;
      this.cepError = 'CEP inválido';
    },

    onNoNumberChange() {
      if (this.form.noNumber) {
        this.form.number.disabled = true;
        this.form.number.value = '';
      } else {
        this.form.number.disabled = false;
      }
    },

    getEnderecoPayload() {
      const payload = {
        zip_code: Utils.sanitizeDigitsString(this.form.zipCode.value),
        state: this.form.state.value,
        city: this.form.city.value,
        district: this.form.neighborhood.value,
        street: this.form.street.value,
        complement: this.form.complement.value,
        number: this.form.noNumber ? 'S/N' : this.form.number.value,
      };
      return payload;
    },

    async getPreAuthorizationSubscriptionBodyRequest() {
      const plan_identifier = this.form.plan.value;
      const payable_with = 'credit_card';
      const custom_variables = [];
      const FormaPagamento = {
        description: 'Meu cartão',
      };
      const ClienteIUGU = {
        email: this.userInfo.email,
        name: (this.userInfo.nome || this.form.cardName.value || '').trim(),
        cpf_cnpj: Utils.sanitizeDigitsString(this.form.cpf.value),
        ...this.getEnderecoPayload(),
      };

      if (this.discountApplied && this.form.discountCoupon) {
        custom_variables.push({
          name: 'cupom_desconto',
          value: this.form.discountCoupon.value,
        });
      }
      this.form.installments.value = this.splittingEnabled ? this.form.installments.value : 1;
      custom_variables.push({
        name: 'num_parcelas',
        value: this.form.installments.value.toString(),
      });

      const paymentToken = await IuguService.createPaymentToken(
        IuguService.createCreditCardObj(
          Utils.sanitizeDigitsString(this.form.cardNumber.value),
          this.form.cardExpirationDate.value,
          this.form.cardVerificationValue.value,
          this.form.cardName.value,
        ),
      );
      FormaPagamento.token = paymentToken;

      return {
        plan_identifier,
        payable_with,
        custom_variables,
        FormaPagamento,
        ClienteIUGU,
      };
    },
    getActivateSubscriptionBodyRequest(preAuthorizationInfo, preAuthorizationResult) {
      const { custom_variables } = preAuthorizationInfo;
      const { subscriptionId } = preAuthorizationResult.data.data;
      return {
        subscriptionId,
        custom_variables,
      };
    },
    trackerSnowplowAndBrazeByUserCardName(form) {
      this.trackSnowplow('sendSnowplowSubscribeEvent', {
        form_element: form.cardName,
        step_name: 'user_card_name',
        step_num: 4,
      });

      this.trackBraze('trackEvent', {
        name: 'subscribe_user_card_name',
        attribute: 'step_name',
        event_name: 'subscribe',
      });
    },
    sendSuccessfulSubscriptionEvent() {
      this.trackSnowplow('trackSubscribe', {
        step_name: 'subscribe_success',
        step_num: 8,
        step_value: 'credit_card',
        failure_reason: null,
      });

      this.trackBraze('trackEvent', {
        name: 'subscribe_success',
        attribute: 'step_name',
        event_name: 'subscribe',
      });
    },
    sendSubscriptionFailureEvent() {
      this.trackGTM('trackSubscribe', {
        event: 'naoCompletouAssinatura',
        category: 'click',
        label: 'naoCompletouAssinatura',
      });
    },
    goToCreditCardCongratulationsScreen() {
      this.navigateToRoute({
        name: 'congrats',
        query: { stepName: 'payment' },
      });
    },
    hideMessageGeneralFailure() {
      this.isGeneralFailure = false;
      this.failure = false;
    },
    trackBrazeWhenLoadPage() {
      this.trackBraze('trackEvent', {
        name: 'iniciou_checkout',
        attribute: 'step_name',
        event_name: 'checkout',
      });

      this.trackBraze('trackEvent', {
        name: 'checkout_credit_card',
        attribute: 'step_name',
        event_name: 'checkout',
      });
    },
    trackBrazeWhenSuccessPay() {
      this.trackBraze('trackEvent', {
        name: 'finalizou_checkout',
        attribute: 'step_name',
        event_name: 'checkout',
      });
    },
    trackBrazeWhenError() {
      this.trackBraze('trackEvent', {
        name: 'erro_pagamento_checkout_cartao_credito',
        attribute: 'step_name',
        event_name: 'checkout',
      });
    },

    changeInstallments() {
      let element = document.getElementById('installments');
      if (element) {
        let optionSelected = element.value - 2 || element.options.length - 2;
        let elementSetting = document.querySelector('.summary-price-description');
        if (elementSetting) elementSetting.textContent = element.options[optionSelected].text;
      }
    },
  },
};
</script>

<style scoped lang="scss" src="./checkout-card.scss" />
